import { CoherencePanel, CoherencePanelSize } from '@coherence-design-system/controls';
import { DefaultButton, PrimaryButton, TextField } from '@fluentui/react';
import { Context } from '@micro-frontend-react/employee-experience/lib/Context';
import { IEmployeeExperienceContext } from '@micro-frontend-react/employee-experience/lib/IEmployeeExperienceContext';
import { useUser } from '@micro-frontend-react/employee-experience/lib/useUser';
import * as React from 'react';
import { Context as ReactContext, FormEvent, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { onRenderServiceTreeIdLabel } from '../../../Common/Components/Labels';
import * as Styled from '../../../Common/Styles/portalPage.styled';

type AddDataOwnerPanelProps = {
  isOpen: boolean;
  dismissPanel(): void;
  setShowConfirmationPopup(val: boolean): void;
  setShowErrorMessage(val: boolean): void;
};

export function AddDataOwnerPanel(props: AddDataOwnerPanelProps): React.ReactElement {
  const { isOpen, dismissPanel, setShowConfirmationPopup, setShowErrorMessage } = props;

  const [dataOwner, setDataOwner] = useState({
    id: uuidv4(),
    name: '',
    description: '',
    type: '',
    requestorUpn: '',
    serviceTreeID: '',
    serviceTreeName: '',
    securityGroup: '',
  });

  const user = useUser();
  const history = useHistory();
  const { httpClient } = React.useContext(Context as ReactContext<IEmployeeExperienceContext>);
  const [dataOwnerNameError, setDataOwnerNameError] = useState<boolean>(false);
  const [descriptionError, setDescriptionError] = useState<boolean>(false);
  const [serviceTreeIDError, setServiceTreeIDError] = useState<boolean>(false);
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(true);
  const [isServiceTreeInfoLoading, setIsServiceTreeInfoLoading] = useState(false);

  // Check form validity after each render
  useEffect(() => {
    if (
      dataOwner.name.length > 0 &&
      dataOwner.description.length > 0 &&
      dataOwner.serviceTreeID.length > 0 &&
      dataOwner.serviceTreeName.length > 0 &&
      dataOwner.securityGroup.length > 0 &&
      !dataOwnerNameError &&
      !descriptionError &&
      !serviceTreeIDError
    ) {
      setIsSubmitButtonDisabled(false);
    } else {
      setIsSubmitButtonDisabled(true);
    }
  });

  function onChangeDataOwnerName(event: FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) {
    setDataOwner((request) => ({ ...request, ['name']: newValue }));
  }

  function onChangeDescription(event: FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) {
    setDataOwner((request) => ({ ...request, ['description']: newValue }));
  }

  function getDataOwnerNameErrorMessage(newValue?: string) {
    if (!newValue) {
      setDataOwnerNameError(true);
      return 'Please enter a valid name with printable characters only.';
    } else if (/^[\x00-\x7F]*$/.test(newValue)) {
      setDataOwnerNameError(false);
      return '';
    } else {
      setDataOwnerNameError(true);
      return 'Please enter a valid name with printable characters only.';
    }
  }

  function getDescriptionErrorMessage(newValue?: string) {
    if (!newValue) {
      setDescriptionError(true);
      return 'Please enter a valid description with printable characters only.';
    } else if (/^[\x00-\x7F]*$/.test(newValue)) {
      setDescriptionError(false);
      return '';
    } else {
      setDescriptionError(true);
      return 'Please enter a valid description with printable characters only.';
    }
  }

  async function getServiceTreeIDErrorMessage(newValue?: string) {
    if (!newValue) {
      setServiceTreeIDError(true);
      return 'Please enter a valid Service Tree ID.';
    }
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(newValue)) {
      setServiceTreeIDError(true);
      return 'Please enter a valid Service Tree ID in GUID format.';
    }
    setIsServiceTreeInfoLoading(true);
    let fetchServiceTreeIdErrorMessage = '';
    await httpClient
      .get(`${__API_BASE_URL__}/common/servicetree/${newValue}`, {
        resource: __CLIENT_ID__,
        headers: {
          'Content-Type': 'application/json',
          'X-Content-Type-Options': 'nosniff',
        },
      })
      .then((response: any) => {
        const serviceTreeName = 'name' in response.data ? response.data.name : response.data.componentName;
        const securityGroup = response.data.featureTeam;
        const serviceTreeAccess = response.data.hasFeatureTeamAccess;

        setDataOwner((request) => ({
          ...request,
          ['serviceTreeID']: newValue,
          ['serviceTreeName']: serviceTreeName,
          ['securityGroup']: securityGroup,
        }));
        setIsServiceTreeInfoLoading(false);
        if (!serviceTreeAccess) {
          setServiceTreeIDError(true);
          fetchServiceTreeIdErrorMessage = `Feature Team access required. You must be listed as a member of the feature team alias in Service Tree. Visit https://microsoftservicetree.com/services/${newValue}/metadata to verify your access.`;
        } else {
          setServiceTreeIDError(false);
        }
      })
      .catch(() => {
        setDataOwner((request) => ({
          ...request,
          ['ServiceTreeComponentName']: '',
          ['ServiceGroupName']: '',
          ['TeamContactEmail']: '',
        }));
        setServiceTreeIDError(true);
        setIsServiceTreeInfoLoading(false);
        fetchServiceTreeIdErrorMessage = 'Invalid Service Tree ID fetched from Service Tree.';
      });
    return fetchServiceTreeIdErrorMessage;
  }

  const onSubmit = () => {
    return new Promise(() => {
      setShowConfirmationPopup(true);
      httpClient
        .post(`${__API_BASE_URL__}/tenant/upsertTenant`, {
          resource: __CLIENT_ID__,
          headers: {
            'Content-Type': 'application/json',
            'X-Content-Type-Options': 'nosniff',
          },
          data: {
            type: 'Tenant',
            id: dataOwner.id,
            name: dataOwner.name,
            description: dataOwner.description,
            requestorUpn: user.name,
            serviceTreeID: dataOwner.serviceTreeID,
            serviceTreeName: dataOwner.serviceTreeName,
            securityGroup: dataOwner.securityGroup,
          },
        })
        .then(() => {
          history.push(`/dataOwners/${dataOwner.id}`);
        })
        .catch(() => {
          setShowErrorMessage(true);
        });
    });
  };

  const reset = () => {
    setDataOwner({
      id: uuidv4(),
      type: '',
      name: '',
      description: '',
      requestorUpn: '',
      serviceTreeID: '',
      serviceTreeName: '',
      securityGroup: '',
    });
    setIsSubmitButtonDisabled(true);
  };

  const submitResetAndDismissPanel = async () => {
    await onSubmit();
  };

  const resetAndDismissPanel = () => {
    reset();
    dismissPanel();
  };

  const onRenderFooterContent = () => {
    return (
      <div>
        <PrimaryButton
          text="Submit"
          disabled={isSubmitButtonDisabled}
          allowDisabledFocus={true}
          onClick={submitResetAndDismissPanel}
        />
        <DefaultButton text="Cancel" allowDisabledFocus={true} onClick={resetAndDismissPanel}>
          Cancel
        </DefaultButton>
      </div>
    );
  };

  const onRenderTitleText = () => {
    return <Styled.PortalPagePanelText>Add Data Owner</Styled.PortalPagePanelText>;
  };

  return (
    <CoherencePanel
      isOpen={isOpen}
      onRenderFooter={onRenderFooterContent}
      onDismiss={dismissPanel}
      onRenderHeader={onRenderTitleText}
      panelSize={CoherencePanelSize.medium}
      closeButtonAriaLabel="Close"
      data-testid="add-data-owner-panel"
    >
      <Styled.PortalPagePanelContainer>
        <TextField
          data-testid="add-data-owner-panel-name"
          label="Data Owner Name"
          id="dataOwnerName"
          placeholder="Enter Custom Name"
          required={true}
          onChange={onChangeDataOwnerName}
          onGetErrorMessage={getDataOwnerNameErrorMessage}
          validateOnLoad={false}
          validateOnFocusOut={true}
        />

        <TextField
          data-testid="add-data-owner-panel-description"
          label="Description"
          id="description"
          placeholder="Enter Custom Description"
          required={true}
          onChange={onChangeDescription}
          onGetErrorMessage={getDescriptionErrorMessage}
          validateOnLoad={false}
          validateOnFocusOut={true}
          multiline={true}
          autoAdjustHeight={true}
        />

        {onRenderServiceTreeIdLabel(isServiceTreeInfoLoading)}
        <TextField
          data-testid="add-data-owner-panel-service-tree-id"
          id="serviceTreeID"
          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
          onGetErrorMessage={getServiceTreeIDErrorMessage}
          validateOnLoad={false}
          validateOnFocusOut={true}
        />

        {!isServiceTreeInfoLoading && !serviceTreeIDError && dataOwner.serviceTreeName.length > 0 && (
          <TextField
            label="Service Tree Name"
            id="serviceTreeName"
            value={dataOwner.serviceTreeName}
            disabled={true}
            styles={{
              field: { color: '#323130' },
              subComponentStyles: { label: { root: { color: '#323130' } } },
            }}
          />
        )}

        {!isServiceTreeInfoLoading && !serviceTreeIDError && dataOwner.serviceTreeName.length > 0 && (
          <TextField
            label="Security Group"
            id="securityGroup"
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            value={dataOwner.securityGroup}
            disabled={true}
            styles={{
              field: { color: '#323130' },
              subComponentStyles: { label: { root: { color: '#323130' } } },
            }}
          />
        )}
      </Styled.PortalPagePanelContainer>
    </CoherencePanel>
  );
}
